import { Briefcase, Building2, Filter, MapPin, Users, X } from 'lucide-react'
import { useMemo } from 'react'
import { useJobGroupClusters } from '@/api/queries/job-group-cluster-queries'
import { useJobGroups } from '@/api/queries/job-group-queries'
import { useOrganization } from '@/api/queries/organization-queries'
import { Button } from '@/components/ui/button'
import { MultiSelect } from '@/components/ui/multi-select'
import { SearchableSelect } from '@/components/ui/searchable-select'
import { Skeleton } from '@/components/ui/skeleton'

/**
 * Modo foco em cascata: Filial → Contrato (CR). Filial e CR aceitam
 * múltipla seleção — dá para escolher um ou mais CRs diretamente sem
 * selecionar a filial deles primeiro. Escolher filiais restringe as
 * opções de CR a elas (facilita achar o contrato certo entre ~150
 * registros); trocar a seleção de filiais remove da seleção de CR
 * qualquer CR que não pertença mais ao escopo.
 */
export interface MatrixScope {
	branchIds: string[]
	crIds: string[]
	jobGroupClusterId: string | null
	jobGroupId: string | null
}

export const ALL_SCOPE: MatrixScope = {
	branchIds: [],
	crIds: [],
	jobGroupClusterId: null,
	jobGroupId: null,
}

interface CommitmentMatrixScopeFilterProps {
	scope: MatrixScope
	onChange: (scope: MatrixScope) => void
}

export function CommitmentMatrixScopeFilter({
	scope,
	onChange,
}: CommitmentMatrixScopeFilterProps) {
	const { data: branches, isLoading } = useOrganization()

	const { data: jobGroupClusters } = useJobGroupClusters()
	const jobGroupClusterOptions = useMemo(
		() => (jobGroupClusters ?? []).map((c) => ({ value: c.id, label: c.name })),
		[jobGroupClusters],
	)

	const { data: jobGroups } = useJobGroups()
	const jobGroupOptions = useMemo(
		() => (jobGroups ?? []).map((g) => ({ value: g.id, label: g.name })),
		[jobGroups],
	)

	const branchOptions = useMemo(
		() => (branches ?? []).map((b) => ({ value: b.id, label: b.name })),
		[branches],
	)

	const crOptions = useMemo(() => {
		if (!branches) return []
		const crs =
			scope.branchIds.length > 0
				? branches
						.filter((b) => scope.branchIds.includes(b.id))
						.flatMap((b) => b.crs)
				: branches.flatMap((b) => b.crs)
		return crs.map((c) => ({ value: c.id, label: c.name }))
	}, [branches, scope.branchIds])

	const handleBranchesChange = (branchIds: string[]) => {
		const validCrIds = new Set(
			branches
				?.filter((b) => branchIds.includes(b.id))
				.flatMap((b) => b.crs.map((c) => c.id)) ?? [],
		)
		const crIds =
			branchIds.length === 0
				? scope.crIds
				: scope.crIds.filter((id) => validCrIds.has(id))
		onChange({ ...scope, branchIds, crIds })
	}

	const handleCrsChange = (crIds: string[]) => {
		onChange({ ...scope, crIds })
	}

	const handleJobGroupClusterChange = (jobGroupClusterId: string) => {
		onChange({
			...scope,
			jobGroupClusterId: jobGroupClusterId || null,
			jobGroupId: jobGroupClusterId ? null : scope.jobGroupId,
		})
	}

	const handleJobGroupChange = (jobGroupId: string) => {
		onChange({
			...scope,
			jobGroupId: jobGroupId || null,
			jobGroupClusterId: jobGroupId ? null : scope.jobGroupClusterId,
		})
	}

	const hasScope =
		scope.branchIds.length > 0 ||
		scope.crIds.length > 0 ||
		scope.jobGroupClusterId ||
		scope.jobGroupId

	if (isLoading) {
		return (
			<div className="flex items-center gap-2 rounded-lg border border-kaefer-light-silver bg-muted/30 px-3 py-2">
				<Skeleton className="h-8 w-full sm:w-56" />
				<Skeleton className="h-8 w-full sm:w-64" />
			</div>
		)
	}

	return (
		<div className="flex flex-col gap-2 rounded-lg border border-kaefer-light-silver bg-muted/30 px-3 py-2 sm:flex-row sm:flex-wrap sm:items-center">
			<div className="flex shrink-0 items-center gap-2">
				<Filter className="size-4 shrink-0 text-kaefer-medium-grey" />
				<span className="text-sm font-medium text-kaefer-dark-grey">
					Modo foco:
				</span>
			</div>

			<div className="flex flex-1 flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
				<MultiSelect
					options={branchOptions}
					values={scope.branchIds}
					onValuesChange={handleBranchesChange}
					placeholder="Todas as filiais"
					searchPlaceholder="Buscar filial..."
					emptyMessage="Nenhuma filial encontrada"
					className="h-8 w-full sm:w-56"
				/>
				<span className="hidden text-kaefer-medium-grey sm:inline">
					<Building2 className="size-3.5" />
				</span>

				<MultiSelect
					options={crOptions}
					values={scope.crIds}
					onValuesChange={handleCrsChange}
					placeholder="Todos os CRs"
					searchPlaceholder="Buscar CR..."
					emptyMessage="Nenhum CR encontrado"
					className="h-8 w-full sm:w-64"
				/>
				<span className="hidden text-kaefer-medium-grey sm:inline">
					<MapPin className="size-3.5" />
				</span>

				<SearchableSelect
					options={jobGroupClusterOptions}
					value={scope.jobGroupClusterId ?? ''}
					onValueChange={handleJobGroupClusterChange}
					placeholder="Todos os clusters"
					searchPlaceholder="Buscar cluster..."
					emptyMessage="Nenhum cluster encontrado"
					className="h-8 w-full sm:w-56"
				/>
				<span className="hidden text-kaefer-medium-grey sm:inline">
					<Users className="size-3.5" />
				</span>

				<SearchableSelect
					options={jobGroupOptions}
					value={scope.jobGroupId ?? ''}
					onValueChange={handleJobGroupChange}
					placeholder="Todos os grupos de cargo (avançado)"
					searchPlaceholder="Buscar grupo..."
					emptyMessage="Nenhum grupo encontrado"
					className="h-8 w-full sm:w-56"
				/>
				<span className="hidden text-kaefer-medium-grey sm:inline">
					<Briefcase className="size-3.5" />
				</span>
			</div>

			{hasScope && (
				<Button
					type="button"
					variant="ghost"
					size="sm"
					onClick={() => onChange(ALL_SCOPE)}
					className="h-8 w-full gap-1 text-kaefer-medium-grey hover:bg-transparent hover:text-kaefer-dark-grey sm:w-auto"
				>
					<X className="size-3.5" />
					Limpar
				</Button>
			)}
		</div>
	)
}
